/**
 * Rendu INLINE du bloc `clue_hunt` — accordéon.
 *   - En-tête : l'intro du bloc (`intro_md`) + icône de repli, entièrement
 *     cliquable pour toggler l'expansion.
 *   - Corps : la liste des indices RÉVÉLÉS (une ligne verte par indice avec
 *     revealed_md + badge XP). Tant qu'aucun indice n'est trouvé, un petit
 *     texte d'invitation. Le compteur N/total reste dans la barre fixed top.
 *
 * Ouvert par défaut ; se replie automatiquement quand le parent passe
 * `autoCollapse=true` (typiquement : la question courante a été répondue).
 */

import { useEffect, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { MarkdownMath, cn } from '@jeraisonne/ui';
import { type Clue, type ClueHuntData } from '@jeraisonne/db';
import { useClueHuntCtx } from './ClueHuntContext';

export interface ClueHuntCasesInlineProps {
  data: ClueHuntData;
  /** Quand true, la carte se replie automatiquement (typ. question passée). */
  autoCollapse?: boolean;
  /** Mode read-only historique : indices révélés (ids) + XP gagnée par indice
      (map). Utilisé pour rendre une chasse TERMINÉE hors provider (dans
      PastStepCard). Si fourni, ignore le context. */
  frozen?: { revealed: string[]; earned: Array<[string, number]> };
}

export function ClueHuntCasesInline({ data, autoCollapse, frozen }: ClueHuntCasesInlineProps) {
  const ctx = useClueHuntCtx();
  // Priorité : le state figé (mode historique) > le state du context (mode live).
  const revealedSet = frozen
    ? new Set(frozen.revealed)
    : (ctx?.state.revealed ?? new Set<string>());
  const earnedMap = frozen
    ? new Map(frozen.earned)
    : (ctx?.state.earned ?? new Map<string, number>());
  const revealedClues = data.clues.filter((c) => revealedSet.has(c.id));

  const [expanded, setExpanded] = useState(true);
  // Repli automatique sur signal externe (ex. question répondue).
  useEffect(() => {
    if (autoCollapse) setExpanded(false);
  }, [autoCollapse]);

  return (
    <section
      className="rounded-2xl border-2 border-cyan-200 bg-cyan-50/50"
      aria-label="Chasse aux indices"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex w-full items-start gap-2 rounded-t-2xl px-4 py-3 text-left transition-colors hover:bg-cyan-100/60 active:scale-[0.99]"
      >
        <Search className="mt-0.5 h-4 w-4 shrink-0 text-cyan-700" />
        <span className="text-ink-900 flex-1 text-sm font-semibold">
          <MarkdownMath>{data.intro_md}</MarkdownMath>
        </span>
        <ChevronDown
          className={cn(
            'mt-0.5 h-4 w-4 shrink-0 text-cyan-700 transition-transform',
            expanded ? 'rotate-180' : '',
          )}
        />
      </button>

      {expanded && (
        <div className="px-4 pb-3">
          {revealedClues.length === 0 ? (
            <p className="text-ink-500 text-xs italic">
              Clique sur les données importantes dans l'énoncé pour les révéler.
            </p>
          ) : (
            <ol className="space-y-1.5">
              {revealedClues.map((clue, i) => (
                <RevealedRow key={clue.id} clue={clue} xp={earnedMap.get(clue.id)} />
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}

/** Une ligne d'indice révélé — utilisée AUSSI par CluePopup (même design). */
export function RevealedRow({ clue, xp }: { clue: Clue; xp: number | undefined }) {
  return (
    <li className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-sm">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
        <Search className="h-3.5 w-3.5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-ink-900 font-medium">
          <MarkdownMath>{clue.revealed_md}</MarkdownMath>
        </span>
      </span>
      {xp !== undefined && (
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold',
            xp > 0 ? 'bg-emerald-200 text-emerald-900' : 'bg-amber-200 text-amber-900',
          )}
        >
          {xp > 0 ? `+${xp}` : '0'}
        </span>
      )}
    </li>
  );
}
